import { extendObservable, observable, action, computed } from 'mobx';
import { snakeCase } from '@ridi/object-case-converter';
import uuid from 'node-uuid';
import { debug } from 'util';
import axios, { setAxiosRootStoreAlertHandler } from '../../../libs/axios_csrf';

import BmcCard from './BmcCard';
import {
  validateCardNumericalFields,
  addBmcCardRelationsAttributes,
  clearCircularStructure,
  manageBmcCardRelationsAttributes,
  updateBmcCardRelationsAttributes
} from '../common/CardHelpers';
import {
  BMC_STAGES,
  BMC_BLOCKS
} from '../../common_components/bmc/bmc_constants';
import { unauthorizedMessageOrProvided } from '../../common_components/alert_helpers';
import { calculateNewPosition } from '../../common_components/functions';

export default class BlockStore {
  constructor(rootStore, { id, name, order, cards }) {
    this.rootStore = rootStore;
    this.name = name;
    this.order = order;
    this.id = id;

    this.tempCard = new BmcCard(this, {});

    // this._cards = cards.map((c) => {
    //     return new BmcCard(this, c)
    // });

    extendObservable(this, {
      // ////////////////////////////////////////////////////////////////////////////////////////////////
      // BEWARE THAT THIS ATTRIBUTE IS ALSO USED IN igbusiness/stores/IgBusinessStore.js
      // Where IgBusinessStore is the rootStore.
      // Make sure not to change any interface
      // ////////////////////////////////////////////////////////////////////////////////////////////////
      cards: cards.map(c => {
        return new BmcCard(this, c);
      }),

      showModal: false,
      editingCard: new BmcCard(this, {}, false),
      isEditingCard: false
    });

    // setAxiosDefaultErrorHandler(this.rootStore);

    // ///////// dev add test card in each block  //////////////
    // this.addCard({name: 'First user:  test card '});
    // this.addCard({name: 'test card 2'});

    // ////////////////////////////
  }

  @computed get cardsLength() {
    return this.cards.length;
  }

  @action
  addCard(card) {
    const bmcCard = this._prepareCard(card);
    bmcCard.bmcBlockId = this.id;

    // ------  AJAX CALL ----
    return axios
      .post('/bmc_cards.json', {
        bmc_card: bmcCard.serialize()
      })
      .then(
        action(response => {
          this.rootStore.bmcUpdate += 1;
          bmcCard.id = response.data.id;
          bmcCard.idd = response.data.idd;
          bmcCard.bmcCardRelations = response.data.bmcCardRelations;
          this.rootStore.attachmentsStore.attachmentsFromJsonArray(
            response.data.attachments,
            bmcCard
          );
          // bmcCard.attachments = response.data.attachments;
          bmcCard.creator = response.data.creator;
          bmcCard.persisted = true;
          bmcCard.persistAttachments();
          bmcCard.originalBmcName = response.data.originalBmcName;
          // console.log("data: ", response.data);
          this.rootStore.generateAlert({
            type: 'success',
            headline: 'card_added_success_alert'
          });
          return bmcCard;
        })
      )
      .catch(
        action(error => {
          // console.log(error);
          this.rootStore.generateAlert({
            type: 'danger',
            headline: unauthorizedMessageOrProvided(error, 'card_error_alert')
          });
          return null;
        })
      );
    //-----------------------------------------------
  }

  @action
  addCloneCard(card) {
    const bmcCard = this._prepareCard(card);
    bmcCard.idd = bmcCard.id;
    bmcCard.bmcBlockId = this.id;

    // ------  AJAX CALL ---- //
    return axios
      .patch(`/bmc_cards/${card.id}/clone`, {
        bmc_card: {
          bmc_block_id: bmcCard.bmcBlockId,
          label: card.label
        }
      })
      .then(
        action(response => {
          bmcCard.id = response.data.id;
          bmcCard.idd = response.data.idd;
          bmcCard.bmcCardRelations = response.data.bmcCardRelations;
          bmcCard.attachments = observable([]);
          this.rootStore.attachmentsStore.attachmentsFromJsonArray(
            response.data.attachments,
            bmcCard
          );
          // bmcCard.attachments = response.data.attachments;
          bmcCard.creator = response.data.creator;
          bmcCard.persisted = true;
          this.rootStore.generateAlert({
            type: 'success',
            headline: 'card_added_success_alert'
          });
          return bmcCard;
        })
      )
      .catch(
        action(error => {
          // console.log(error);
          this.rootStore.generateAlert({
            type: 'danger',
            headline: unauthorizedMessageOrProvided(error, 'card_error_alert')
          });
          return null;
        })
      );
    //-----------------------------------------------
  }

  @action
  _prepareCard(card) {
    card = this.addStageLabelToCard(card);
    this.cards.unshift(card);
    validateCardNumericalFields(card);
    manageBmcCardRelationsAttributes(card);
    updateBmcCardRelationsAttributes(card, card.bmcCardRelations);

    return card;
  }

  @action
  addTemplateCards(cardTemplate) {
    // Pass template instead
    const card = this.cards.find(
      el =>
        el.name == cardTemplate.name &&
        el.description == cardTemplate.description
    );
    if (!card) {
      return this.addCard(cardTemplate);
    }
    return new Promise((resolve, reject) => {
      resolve(card);
    });
  }

  @action
  updateCardPosition(card, index, blockChanged) {
    card.position = calculateNewPosition(this.cards, index);

    // ------  AJAX CALL ----------------------------
    axios
      .patch(`/bmc_cards/${card.id}.json`, {
        bmc_card: {
          position: card.position,
          bmc_block_id: blockChanged ? this.id : undefined
        }
      })
      .then(
        action(response => {
          // console.log("response: ", response);
          this.rootStore.generateAlert({
            type: 'success',
            headline: 'card_updated_success_alert'
          });
        })
      )
      .catch(
        action(error => {
          // console.log(error.response);
          this.rootStore.generateAlert({
            type: 'danger',
            headline: unauthorizedMessageOrProvided(error, 'card_error_alert')
          });
        })
      );
    //------------------------------------------------
  }

  // Moves the given note to a target lane
  @action
  moveCardFromDropdown(card) {
    const cardIndex = card.store.cards.indexOf(card);
    card.store.cards.splice(cardIndex, 1);

    this.cards.splice(0, 0, card);
    card.store = this;

    card.position = this.cards[0] ? this.cards[0].position - 0.1 : 0;

    // ------  AJAX CALL ----------------------------
    axios
      .patch(`/bmc_cards/${card.id}.json`, {
        bmc_card: {
          bmc_block_id: this.id,
          position: card.position
        }
      })
      .then(
        action(response => {
          this.rootStore.generateAlert({
            type: 'success',
            headline: 'card_updated_success_alert'
          });
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );
    //------------------------------------------------
  }

  // ////////////////////////////////////////////////////////////////////////////////////////////////
  // BEWARE THAT THIS METHOD IS ALSO USED IN igbusiness/stores/IgBusinessStore.js
  // Where IgBusinessStore is the rootStore.
  // Make sure to either check if the rootStore is an IgMEASURE component (rootStore.isIgMeasure) or
  // that the IgBusinessStore has any attribute or method invoked for the rootStore.
  // ////////////////////////////////////////////////////////////////////////////////////////////////
  filteredCards(
    rootStore = this.rootStore,
    name = this.name,
    cards = this.cards
  ) {
    // BMC Vision in observational stage doesn't imply filters, return original cards array
    if (
      (rootStore.isBmcVision &&
        rootStore.thinkingMode &&
        rootStore.thinkingMode.atBmcVision) ||
      (rootStore.isBmcVision && rootStore.isIgMeasure)
    ) {
      return cards;
    }

    // Value proposition blocks doesn't imply filters, return original cards array
    if (name === BMC_BLOCKS.KEY_PROPOSITIONS) {
      if (rootStore.blankSlateMode) {
        return cards.filter(card => card.id === card.idd);
      }
      return cards;
    }

    const { filters } = rootStore;

    const filterLabels = Object.keys(filters).map((key, index) => {
      if (filters[key] && key !== BMC_STAGES.FREE) {
        return key;
      }
    });

    if (this.id == 7661) {
      // debugger
    }

    const bmcStagesExceptFree = Object.values(BMC_STAGES).filter(
      stage => stage !== BMC_STAGES.FREE
    );

    // use secondary current stage if its in bmc thinking mode, as the currentStage doesn't update
    let { currentStage } = rootStore;
    if (
      rootStore.isBmcVision &&
      rootStore.thinkingMode.active &&
      !rootStore.thinkingMode.atBmcVision
    ) {
      currentStage = rootStore.secondaryCurrentStage;
    }
    // ////////////////////////////////////////////////////////////////////////////////////////////////////

    if (rootStore.blankSlateMode) {
      return cards.filter(card => {
        return (
          //card.id === card.idd &&  Fixed Luis C. 07/08/2025
          this._filterCard(
            card,
            rootStore,
            filters,
            bmcStagesExceptFree,
            filterLabels
          )
        );
      });
    }

    return cards.filter(card => {
      return this._filterCard(
        card,
        rootStore,
        filters,
        bmcStagesExceptFree,
        filterLabels
      );
    });
  }

  _filterCard(card, rootStore, filters, bmcStagesExceptFree, filterLabels) {
    return (
      ((rootStore.isCurrentStageFree || filters[BMC_STAGES.FREE]) &&
        !bmcStagesExceptFree.includes(card.label)) ||
      // When in BMC MVP, filter cards by current stage. Avoid
      // filtering cards by current stage when in BMC Vision
      (!(rootStore.thinkingMode && rootStore.thinkingMode.active) &&
        card.label === rootStore.currentStage) ||
      // TO DO: DOCUMENT
      filterLabels.includes(card.label) ||
      // Filter cards by secondary stage when in BMC vision
      (rootStore.thinkingMode &&
        rootStore.thinkingMode.active &&
        card.label === rootStore.secondaryCurrentStage)
    );
  }

  // updateFilteredCards() {
  //     this.cardsAfterFilters = this.filteredCards();
  // }

  @action
  showBlockModal(show) {
    this.showModal = show;
    this.editingCard = new BmcCard(this, {}, false);
    this.isEditingCard = false;
  }

  @action
  hideBlockModal(hide) {
    this.showBlockModal(!hide);
    this.editingCard = new BmcCard(this, {}, false);
    this.isEditingCard = false;
  }

  @action
  showEditingCardBlockModal(card) {
    this.showModal = true;
    this.editingCard = card;
    this.isEditingCard = true;
  }

  @action
  addStageLabelToCard(card) {
    // disable in Bmc Vision
    if (this.rootStore.isBmcVision) {
      card.label = BMC_STAGES.OBSERVATIONAL;
      // return card;
      // } else if (!this.rootStore.isCurrentStageFree) {
    } else {
      card.label = this.rootStore.currentStage;
    }
    return card;
  }

  @computed get notCSorVP() {
    return (
      this.name !== BMC_BLOCKS.KEY_PROPOSITIONS &&
      this.name !== BMC_BLOCKS.CUSTOMER_SEGMENTS
    );
  }

  // get vision's bmc cards of the same block (using order instead of name).
  @computed get getVisionCards() {
    let cards = [];
    const visionBmcBlock = this.rootStore.visionBmcBlocks.find(
      b => b.order === this.order
    );

    if (visionBmcBlock) {
      cards = visionBmcBlock.cards;
    } else {
      // If visionBmcBlock is empty, get data from backend.
      this.rootStore.fetchVisionData();
    }
    return cards;
  }

  // toJSON() {
  //     return {
  //         id: this.id,
  //         name: this.name,
  //         order: this.order,
  //         cards: this.cards,
  //         updatedAt: this.updatedAt,
  //     };
  // }
}
